import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Card from "@/components/ui/Card";
import Badge from "@/components/ui/Badge";
import EmptyState from "@/components/ui/EmptyState";
import { ButtonLink } from "@/components/ui/Button";
import { Link, usePage } from "@inertiajs/react";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { PublicProps } from "@/types";

function EventCard({
  event,
  going,
  seasonOver = false,
}: {
  event: SatelliteEvent;
  going: boolean;
  seasonOver?: boolean;
}) {
  return (
    <Link href={`/events/${event.id}`}>
      <Card interactive>
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-display text-heading text-ink leading-tight font-bold">
            {event.name}
          </h2>
          {going && <Badge tone="success">Going</Badge>}
        </div>

        <p className="text-body text-ink-muted mt-1">{event.location_label}</p>

        <p className="text-body text-ink mt-3 font-bold">
          {event.starts_at_short_label ??
            (event.awaiting_new_date ? "New date coming" : "Date TBD")}
        </p>

        {event.awaiting_new_date && (
          <p className="text-caption text-ink-muted mt-1">
            {event.postponed_from_label
              ? `Moved from ${event.postponed_from_label}`
              : "Moved to October"}
          </p>
        )}

        {event.description && (
          <p className="text-body text-ink-muted mt-2 line-clamp-2">
            {event.description}
          </p>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-2 pt-4">
          <Badge tone="neutral">
            {event.rsvp_count ?? 0} {seasonOver ? "signed up" : "going"}
          </Badge>
          {seasonOver ? null : event.full ? (
            <Badge tone="warning">Full</Badge>
          ) : (
            event.spots_left != null && (
              <Badge tone="info">{event.spots_left} spots left</Badge>
            )
          )}
          {event.organizer_name && (
            <span className="text-caption text-ink-soft">
              by {event.organizer_name}
            </span>
          )}
        </div>
      </Card>
    </Link>
  );
}

export default function EventsIndex({
  upcoming,
  awaiting_new_date,
  past,
  my_rsvp_event_ids,
}: {
  upcoming: SatelliteEvent[];
  // Approved and moved to October, waiting on their organizer to pick a day.
  // Their own section rather than mixed into `upcoming`, which is sorted by a
  // date these don't have.
  awaiting_new_date: SatelliteEvent[];
  past: SatelliteEvent[];
  my_rsvp_event_ids: number[];
}) {
  const { props } = usePage<PublicProps>();
  const going = new Set(my_rsvp_event_ids);
  // Closing the organizer boards is how HQ ends a season, and then this page
  // is the record of where it happened rather than a list to sign up from.
  const seasonOver = !props.program.organizer_boards_open;

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          title="Events"
          subtitle={
            seasonOver
              ? `Boba Bash is over for this year. Here's every city that ran one: ${past.length} in all.`
              : props.program.rsvps_open
                ? "Every Boba Bash happening this season. Signing up is free."
                : "Every Boba Bash happening this season. Sign-ups aren't open yet."
          }
          actions={
            props.program.organizer_applications_open && props.user ? (
              <ButtonLink href="/organize" variant="secondary">
                Run your own
              </ButtonLink>
            ) : undefined
          }
        />

        {seasonOver ? null : upcoming.length === 0 &&
          awaiting_new_date.length === 0 ? (
          <EmptyState
            title="No events scheduled yet"
            description="No city has a date locked in yet. They show up here as soon as they do."
            action={
              props.user ? (
                <ButtonLink href="/organize">Apply to organize</ButtonLink>
              ) : undefined
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {upcoming.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                going={going.has(event.id)}
              />
            ))}
          </div>
        )}

        {awaiting_new_date.length > 0 && (
          <section className="flex flex-col gap-4">
            <div>
              <h2 className="font-display text-heading text-ink font-bold">
                Picking a new date
              </h2>
              <p className="text-body text-ink-muted mt-1">
                These cities moved to October so their organizers get more time.
                They're still happening - the day just isn't locked in yet.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {awaiting_new_date.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  going={going.has(event.id)}
                />
              ))}
            </div>
          </section>
        )}

        {past.length > 0 && (
          <section className="flex flex-col gap-4">
            <h2 className="font-display text-heading text-ink font-bold">
              {seasonOver ? "Where it happened" : "Already happened"}
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {past.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  going={going.has(event.id)}
                  seasonOver={seasonOver}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
